<template>
  <v-container class="sign-in fill-height" fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="10" md="5" lg="4">
        <v-card class="sign-in__card" flat outlined>
          <div class="sign-in__header">
            <div class="sign-in__logo-wrap">
              <v-img
                :src="require('../../public/logoSignln.png')"
                class="sign-in__logo"
                contain
                max-height="48"
              />
            </div>
            <h1 class="sign-in__title">Авторизация</h1>
            <p class="sign-in__subtitle">Введите данные для входа в систему</p>
          </div>
          <v-card-text class="sign-in__body">
            <v-text-field
              ref="emailField"
              v-model="email"
              :error-messages="emailErrors"
              label="E-mail"
              placeholder="example@mail.ru"
              autocomplete="username"
              outlined
              dense
              flat
              prepend-inner-icon="mdi-email-outline"
              class="sign-in__field"
              required
              @input="$v.email.$touch()"
              @blur="$v.email.$touch()"
              @keyup.enter="submit"
            />
            <v-text-field
              ref="passwordField"
              v-model="password"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              label="Пароль"
              placeholder="Пароль"
              autocomplete="current-password"
              outlined
              dense
              flat
              prepend-inner-icon="mdi-lock-outline"
              class="sign-in__field"
              :type="showPassword ? 'text' : 'password'"
              @click:append="showPassword = !showPassword"
              @keyup.enter="submit"
            />
          </v-card-text>
          <v-card-actions class="sign-in__actions">
            <v-btn
              color="primary"
              class="text-none sign-in__btn"
              large
              rounded
              :loading="loading"
              :disabled="$v.$anyError || oauthLoading"
              @click.prevent="submit"
            >
              <v-icon left size="22">mdi-login</v-icon>
              Войти
            </v-btn>
            <template v-if="oauthEnabled">
              <div class="sign-in__divider">
                <span>или</span>
              </div>
              <v-btn
                outlined
                color="primary"
                class="text-none sign-in__btn sign-in__btn--evo"
                large
                rounded
                :loading="oauthLoading"
                :disabled="loading"
                @click.prevent="loginWithEvoId"
              >
                <v-icon left size="22">mdi-shield-account</v-icon>
                Войти через ЭВО ID
              </v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required, email } from 'vuelidate/lib/validators';
import UserService from '@/services/user-service';
import {
  isOAuthConfigured,
  startEvoIdLogin,
} from '@/services/oauth';
import { store } from '@/main';
import ACL from '@/services/ACL';
import { mapActions } from 'vuex';

export default {
  name: 'SignIn',
  mixins: [validationMixin],
  validations: {
    email: { required, email },
  },
  data () {
    return {
      showPassword: false,
      email: '',
      password: '',
      loading: false,
      oauthLoading: false,
    };
  },
  computed: {
    oauthEnabled () {
      return isOAuthConfigured();
    },
    emailErrors () {
      const errors = [];
      if (!this.$v.email.$dirty) return errors;
      !this.$v.email.email && errors.push('Введите корректный e-mail');
      !this.$v.email.required && errors.push('E-mail обязателен');
      return errors;
    },
  },
  mounted () {
    this.syncAutofilledValues();
    setTimeout(this.syncAutofilledValues, 120);
    setTimeout(this.syncAutofilledValues, 400);
  },
  methods: {
    ...mapActions({
      loadAllAgents: 'Agents/loadAllAgents',
      getAllSettingsTables: 'User/getAllSettingsTables',
    }),
    async loginWithEvoId () {
      this.oauthLoading = true;
      try {
        await startEvoIdLogin();
      } catch (err) {
        this.$flash_warning(err.message || 'Не удалось начать вход через ЭВО ID.');
        this.oauthLoading = false;
      }
    },
    syncAutofilledValues () {
      const emailInput = this.$refs.emailField?.$el?.querySelector('input');
      const passwordInput = this.$refs.passwordField?.$el?.querySelector('input');
      if (emailInput && emailInput.value && emailInput.value !== this.email) {
        this.email = emailInput.value;
        this.$v.email.$touch();
      }
      if (passwordInput && passwordInput.value && passwordInput.value !== this.password) {
        this.password = passwordInput.value;
      }
    },
    submit () {
      this.$v.$touch();
      if (this.$v.$anyError) return false;
      this.loading = true;
      UserService.instance.auth({
        email: this.email,
        password: this.password,
      })
        .then(() => {
          this.loadAllAgents();
          this.getAllSettingsTables();
          this.redirectAfterLoginIfAllowed();
        })
        .catch((err) => {
          const serverError = err.response?.data?.error;
          let message = 'Ошибка авторизации.';
          if (serverError === 'invalid_credentials') {
            message = 'Введены неверные учётные данные. Проверьте почту и пароль.';
          } else if (serverError) {
            message = serverError;
          }
          this.$flash_warning(message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async redirectAfterLoginIfAllowed () {
      const roleName = store.getters.roleName;
      const permissions = ACL.roles[roleName]?.routes || [];
      const currentPath = store.state.redirectAfterLogin;
      const matchedRoute = this.$router.match(currentPath);

      if (matchedRoute && permissions.includes(matchedRoute.name)) {
        if (this.$route.fullPath !== currentPath) {
          await this.$router.push(currentPath);
        }
      } else {
        await this.$router.push({ name: 'Home' });
      }
      await store.dispatch('clearRedirect');
    },
  },
};
</script>

<style lang="scss" scoped>
.sign-in {
  background: linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 50%, #fff8e1 100%);
  min-height: 100vh;
}

.sign-in__card {
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.08),
    0 10px 20px -4px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.25s ease;
}

.sign-in__card:hover {
  box-shadow:
    0 12px 24px -8px rgba(0, 0, 0, 0.12),
    0 20px 40px -12px rgba(0, 0, 0, 0.14),
    0 0 0 1px rgba(0, 0, 0, 0.04);
}

.sign-in__header {
  padding: 36px 24px 28px;
  text-align: center;
  background: linear-gradient(180deg, rgba(25, 118, 210, 0.06) 0%, rgba(255, 255, 255, 0) 100%);
  border-bottom: 1px solid rgba(25, 118, 210, 0.08);
}

.sign-in__logo-wrap {
  margin-bottom: 20px;
}

.sign-in__logo {
  margin: 0 auto;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.06));
}

.sign-in__title {
  font-size: 1.625rem;
  font-weight: 600;
  color: #1565c0;
  margin: 0 0 6px 0;
  letter-spacing: -0.02em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.sign-in__subtitle {
  font-size: 0.9375rem;
  color: #546e7a;
  margin: 0;
}

.sign-in__body {
  padding: 28px 24px;
}

.sign-in__field {
  margin-bottom: 12px;
}

.sign-in__field:last-of-type {
  margin-bottom: 0;
}

.sign-in__field ::v-deep .v-input__prepend-inner .v-icon {
  color: rgba(0, 0, 0, 0.5);
}

.sign-in__field ::v-deep input:-webkit-autofill,
.sign-in__field ::v-deep input:-webkit-autofill:hover,
.sign-in__field ::v-deep input:-webkit-autofill:focus {
  -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
  -webkit-box-shadow: 0 0 0 1000px #e9f1fb inset !important;
  box-shadow: 0 0 0 1000px #e9f1fb inset !important;
  caret-color: rgba(0, 0, 0, 0.87);
}

.sign-in__actions {
  padding: 8px 24px 32px;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}

.sign-in__divider {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 220px;
  margin: 16px 0 12px;
  color: #90a4ae;
  font-size: 0.8125rem;
}

.sign-in__divider::before,
.sign-in__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(25, 118, 210, 0.16);
}

.sign-in__divider span {
  padding: 0 12px;
}

.sign-in__btn {
  min-width: 220px;
  padding: 12px 32px !important;
  font-size: 1.0625rem !important;
  font-weight: 600;
  box-shadow:
    0 2px 8px rgba(25, 118, 210, 0.35),
    0 4px 12px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.sign-in__btn:hover:not(.v-btn--disabled) {
  box-shadow:
    0 6px 16px rgba(25, 118, 210, 0.4),
    0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.sign-in__btn:active:not(.v-btn--disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(25, 118, 210, 0.3);
}

.sign-in__btn--evo {
  box-shadow: none;
}

.sign-in__btn--evo:hover:not(.v-btn--disabled),
.sign-in__btn--evo:active:not(.v-btn--disabled) {
  box-shadow: none;
  transform: none;
}
</style>
